import {
    createChallengeGroupFailure,
    createChallengeGroupRequest,
    createChallengeGroupSuccess,
} from '@actions/challengeGroups/createChallengeGroup';
import {
    deleteChallengeGroupFailure,
    deleteChallengeGroupRequest,
    deleteChallengeGroupSuccess,
} from '@actions/challengeGroups/deleteChallengeGroup';
import {
    editChallengeGroupFailure,
    editChallengeGroupRequest,
    editChallengeGroupSuccess,
} from '@actions/challengeGroups/editChallengeGroup';
import {
    editChallengeGroupScoreWeightingFailure,
    editChallengeGroupScoreWeightingRequest,
    editChallengeGroupScoreWeightingSuccess,
} from '@actions/challengeGroups/editChallengeGroupScoreWeighting';
import {
    editChallengeHolderFailure,
    editChallengeHolderRequest,
    editChallengeHolderSuccess,
} from '@actions/challengeGroups/editChallengeHolder';
import {
    fetchChallengeGroupFailure,
    fetchChallengeGroupRequest,
    fetchChallengeGroupSuccess,
} from '@actions/challengeGroups/fetchChallengeGroup';
import {
    fetchChallengeGroupsFailure,
    fetchChallengeGroupsRequest,
    fetchChallengeGroupsSuccess,
} from '@actions/challengeGroups/fetchChallengeGroups';
import {
    fetchChallengeHoldersForGroupFailure,
    fetchChallengeHoldersForGroupRequest,
    fetchChallengeHoldersForGroupSuccess,
} from '@actions/challengeGroups/fetchChallengeHoldersForGroup';
import {
    populateChallengeGroupAdminsRequest,
    populateChallengeGroupAdminsSuccess,
    populateChallengeGroupAdminsFailure,
    populateChallengeGroupViewersRequest,
    populateChallengeGroupViewersSuccess,
    populateChallengeGroupViewersFailure,
    ChallengeAdminOptionPayload,
    ChallengeViewerOptionPayload,
    populateCreateChallengeGroupAdminsRequest,
    populateCreateChallengeGroupAdminsSuccess,
    populateCreateChallengeGroupAdminsFailure,
    populateCreateChallengeGroupViewersRequest,
    populateCreateChallengeGroupViewersSuccess,
    populateCreateChallengeGroupViewersFailure,
} from '@actions/challengeGroups';
import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import { convertArrToObj } from 'lib/src/utils/generic';

import { ChallengeGroup } from 'src/types/shared/ChallengeGroup';
import UserRoleResponse from 'src/types/shared/UserRoleResponse';
import { ChallengeAdminOption } from 'src/types/shared/AdminUser';
import { ChallengeViewerOption } from 'src/types/shared/User';

interface ChallengeGroupState {
    isFetching: boolean;
    fetchError: string | null;
    isPosting: boolean;
    postSuccess: boolean;
    postError: string | null;
    isDeleting: boolean;
    deleteSuccess: boolean;
    deleteError: string | null;
    isPatching: boolean;
    patchSuccess: boolean;
    patchError: string | null;
    challengeGroups: Record<number, ChallengeGroup>;
    challengeHolders: UserRoleResponse[];
    challengeGroupAdmins: ChallengeAdminOption[];
    challengeGroupViewers: ChallengeViewerOption[];
}

const initialState: ChallengeGroupState = {
    isFetching: false,
    fetchError: null,
    isPosting: false,
    postSuccess: false,
    postError: null,
    isDeleting: false,
    deleteSuccess: false,
    deleteError: null,
    isPatching: false,
    patchSuccess: false,
    patchError: null,
    challengeGroups: {},
    challengeHolders: [],
    challengeGroupAdmins: [],
    challengeGroupViewers: [],
};

export default createReducer(initialState, {
    [fetchChallengeGroupsRequest.type]: handleFetchRequest,
    [fetchChallengeGroupsSuccess.type]: handleFetchAllChallengeGroupsSuccess,
    [fetchChallengeGroupsFailure.type]: handleFetchFailure,
    [fetchChallengeGroupRequest.type]: handleFetchRequest,
    [fetchChallengeGroupSuccess.type]: handleFetchSingleChallengeGroupSuccess,
    [fetchChallengeGroupFailure.type]: handleFetchFailure,
    [deleteChallengeGroupRequest.type]: handleDeleteRequest,
    [deleteChallengeGroupSuccess.type]: handleDeleteSuccess,
    [deleteChallengeGroupFailure.type]: handleDeleteFailure,
    [editChallengeGroupRequest.type]: handlePostRequest,
    [editChallengeGroupFailure.type]: handlePostFailure,
    [editChallengeGroupSuccess.type]: handlePostSuccess,
    [createChallengeGroupRequest.type]: handlePostRequest,
    [createChallengeGroupFailure.type]: handlePostFailure,
    [createChallengeGroupSuccess.type]: handlePostSuccess,
    [editChallengeGroupScoreWeightingRequest.type]: handlePatchRequest,
    [editChallengeGroupScoreWeightingFailure.type]: handlePatchFailure,
    [editChallengeGroupScoreWeightingSuccess.type]: handlePatchSuccess,
    [fetchChallengeHoldersForGroupRequest.type]: handleFetchRequest,
    [fetchChallengeHoldersForGroupSuccess.type]: handleFetchHoldersSuccess,
    [fetchChallengeHoldersForGroupFailure.type]: handleFetchFailure,
    [editChallengeHolderRequest.type]: handlePostRequest,
    [editChallengeHolderFailure.type]: handlePostFailure,
    [editChallengeHolderSuccess.type]: handlePostHolderSuccess,
    [populateChallengeGroupAdminsRequest.type]: handleFetchRequest,
    [populateChallengeGroupAdminsSuccess.type]: handlePopulateChallengeGroupAdminsSuccess,
    [populateChallengeGroupAdminsFailure.type]: handleFetchFailure,
    [populateChallengeGroupViewersRequest.type]: handleFetchRequest,
    [populateChallengeGroupViewersSuccess.type]: handlePopulateChallengeGroupViewersSuccess,
    [populateChallengeGroupViewersFailure.type]: handleFetchFailure,
    [populateCreateChallengeGroupAdminsRequest.type]: handleFetchRequest,
    [populateCreateChallengeGroupAdminsSuccess.type]: handlePopulateCreateChallengeGroupAdminsSuccess,
    [populateCreateChallengeGroupAdminsFailure.type]: handleFetchFailure,
    [populateCreateChallengeGroupViewersRequest.type]: handleFetchRequest,
    [populateCreateChallengeGroupViewersSuccess.type]: handlePopulateCreateChallengeGroupViewersSuccess,
    [populateCreateChallengeGroupViewersFailure.type]: handleFetchFailure,
});

function handleFetchRequest(state: ChallengeGroupState) {
    state.isFetching = true;
    state.fetchError = null;
}

function handlePostRequest(state: ChallengeGroupState) {
    state.isPosting = true;
    state.postSuccess = false;
    state.postError = null;
}

function handleFetchFailure(state: ChallengeGroupState, action: PayloadAction<string>) {
    state.fetchError = action.payload;
    state.isFetching = false;
}
function handlePostFailure(state: ChallengeGroupState, action: PayloadAction<string>) {
    state.postError = action.payload;
    state.isPosting = false;
}

function handlePostSuccess(state: ChallengeGroupState, action: PayloadAction<ChallengeGroup>) {
    state.challengeGroups[action.payload.id] = action.payload;
    state.isPosting = false;
    state.postSuccess = true;
}
function handlePostHolderSuccess(state: ChallengeGroupState) {
    state.isPosting = false;
    state.postSuccess = true;
}

function handleDeleteRequest(state: ChallengeGroupState, action: PayloadAction<number>) {
    state.isDeleting = true;
    state.deleteSuccess = false;
    state.deleteError = null;
}
function handleDeleteFailure(state: ChallengeGroupState, action: PayloadAction<string>) {
    state.isDeleting = false;
    state.deleteError = action.payload;
}
function handleDeleteSuccess(state: ChallengeGroupState, action: PayloadAction<number>) {
    state.isDeleting = false;
    delete state.challengeGroups[action.payload];
    state.deleteSuccess = true;
}
function handlePatchRequest(state: ChallengeGroupState, action: PayloadAction<number>) {
    state.isDeleting = true;
    state.patchSuccess = false;
    state.patchError = null;
}
function handlePatchFailure(state: ChallengeGroupState, action: PayloadAction<string>) {
    state.isDeleting = false;
    state.patchError = action.payload;
}
function handlePatchSuccess(state: ChallengeGroupState, action: PayloadAction<ChallengeGroup>) {
    state.isDeleting = false;
    state.challengeGroups[action.payload.id] = action.payload;
    state.patchSuccess = true;
}

function handleFetchAllChallengeGroupsSuccess(
    state: ChallengeGroupState,
    action: PayloadAction<ChallengeGroup[]>,
) {
    state.challengeGroups = convertArrToObj(action.payload);
    state.isFetching = false;
}
function handleFetchSingleChallengeGroupSuccess(
    state: ChallengeGroupState,
    action: PayloadAction<ChallengeGroup>,
) {
    state.challengeGroups[action.payload.id] = action.payload;
    state.isFetching = false;
}

function handleFetchHoldersSuccess(
    state: ChallengeGroupState,
    action: PayloadAction<UserRoleResponse[]>,
) {
    state.challengeHolders = action.payload;
    state.isFetching = false;
}
function handlePopulateChallengeGroupAdminsSuccess(
    state: ChallengeGroupState,
    action: PayloadAction<ChallengeAdminOptionPayload>,
) {
    state.challengeGroupAdmins = action.payload.users;
    state.isFetching = false;
}
function handlePopulateChallengeGroupViewersSuccess(
    state: ChallengeGroupState,
    action: PayloadAction<ChallengeViewerOptionPayload>,
) {
    state.challengeGroupViewers = action.payload.users;
    state.isFetching = false;
}
function handlePopulateCreateChallengeGroupAdminsSuccess(
    state: ChallengeGroupState,
    action: PayloadAction<ChallengeAdminOption[]>,
) {
    state.challengeGroupAdmins = action.payload;
    state.isFetching = false;
}
function handlePopulateCreateChallengeGroupViewersSuccess(
    state: ChallengeGroupState,
    action: PayloadAction<ChallengeViewerOption[]>,
) {
    state.challengeGroupViewers = action.payload;
    state.isFetching = false;
}
